import React from "react";
import { Box, Paper, Grid, Skeleton, alpha, useTheme } from "@mui/material";

export const StaffBuilderLoading: React.FC = () => {
  const theme = useTheme();

  const HeaderSkeleton = () => (
    <Box sx={{ mb: 4 }}>
      <Skeleton
        variant="rectangular"
        height={32}
        width={280}
        sx={{
          mb: 1,
          borderRadius: 1,
        }}
      />
      <Skeleton
        variant="rectangular"
        height={20}
        width={220}
        sx={{
          borderRadius: 1,
        }}
      />
    </Box>
  );

  const SectionSkeleton = () => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: "1px solid",
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
        borderRadius: "12px",
        height: "100%",
      }}
    >
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
        <Skeleton variant="circular" width={24} height={24} />
        <Skeleton
          variant="rectangular"
          height={24}
          width={180}
          sx={{
            borderRadius: 1,
          }}
        />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {[...Array(3)].map((_, index) => (
          <Box
            key={index}
            sx={{ display: "flex", flexDirection: "column", gap: 1 }}
          >
            <Skeleton
              variant="rectangular"
              height={16}
              width={120}
              sx={{
                borderRadius: 0.5,
              }}
            />
            <Skeleton
              variant="rectangular"
              height={40}
              sx={{
                borderRadius: 1,
              }}
            />
          </Box>
        ))}
      </Box>
    </Paper>
  );

  const ActionButtonsSkeleton = () => (
    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
      <Skeleton
        variant="rectangular"
        width={100}
        height={36}
        sx={{
          borderRadius: 1,
        }}
      />
      <Skeleton
        variant="rectangular"
        width={160}
        height={36}
        sx={{
          borderRadius: 1,
        }}
      />
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <HeaderSkeleton />

      <Grid container spacing={3}>
        {/* Personal Information & Role Access */}
        <Grid item xs={12} md={6}>
          <SectionSkeleton />
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionSkeleton />
        </Grid>

        {/* System Access */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: "1px solid",
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
              borderRadius: "12px",
            }}
          >
            <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton
                variant="rectangular"
                height={24}
                width={180}
                sx={{
                  borderRadius: 1,
                }}
              />
            </Box>
            <Grid container spacing={2}>
              {[1, 2].map((item) => (
                <Grid item xs={12} md={6} key={item}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderColor: (theme) =>
                        alpha(theme.palette.primary.main, 0.1),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        width={40}
                        height={40}
                        sx={{
                          borderRadius: 1,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton
                          variant="rectangular"
                          height={20}
                          width="60%"
                          sx={{
                            borderRadius: 0.5,
                            mb: 0.5,
                          }}
                        />
                        <Skeleton
                          variant="rectangular"
                          height={16}
                          width="80%"
                          sx={{
                            borderRadius: 0.5,
                          }}
                        />
                      </Box>
                      <Skeleton
                        variant="rectangular"
                        width={40}
                        height={20}
                        sx={{
                          borderRadius: 4,
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Facility Assignment */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: "1px solid",
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
              borderRadius: "12px",
            }}
          >
            <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton
                variant="rectangular"
                height={24}
                width={180}
                sx={{
                  borderRadius: 1,
                }}
              />
            </Box>
            <Grid container spacing={2}>
              {[1, 2].map((item) => (
                <Grid item xs={12} md={6} key={item}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      height: 200,
                      borderColor: (theme) =>
                        alpha(theme.palette.primary.main, 0.1),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Skeleton variant="circular" width={24} height={24} />
                      <Skeleton
                        variant="rectangular"
                        height={20}
                        width={140}
                        sx={{
                          borderRadius: 0.5,
                        }}
                      />
                      <Skeleton
                        variant="rectangular"
                        width={80}
                        height={28}
                        sx={{
                          borderRadius: 1,
                          ml: "auto",
                        }}
                      />
                    </Box>
                    {[1, 2, 3].map((line) => (
                      <Skeleton
                        key={line}
                        variant="rectangular"
                        height={40}
                        sx={{
                          borderRadius: 1,
                          mb: 1,
                        }}
                      />
                    ))}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <ActionButtonsSkeleton />
    </Box>
  );
};
